import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { whatsappApi } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';
import './ConnectWhatsAppPage.css';

const SDK_SRC = 'https://embed.chakrahq.com/whatsapp-partner-connect/v1_0_1/sdk.js';

function loadSdk() {
  return new Promise((resolve, reject) => {
    if (window.ChakraWhatsappConnect) return resolve();
    const existing = document.querySelector(`script[src="${SDK_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Chakra SDK')));
      return;
    }
    const s = document.createElement('script');
    s.src = SDK_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Chakra SDK'));
    document.head.appendChild(s);
  });
}

const phoneFromResult = (result) => {
  const list = result?.whatsappPhoneNumbers;
  if (!Array.isArray(list) || list.length === 0) return null;
  const p = list[0];
  if (typeof p === 'string') return p;
  return (
    p.display_phone_number || p.displayPhoneNumber || p.phoneNumber || p.phone_number || null
  );
};

const ConnectWhatsAppPage = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const instanceRef = useRef(null);

  const [status, setStatus] = useState('loading'); // loading | ready | success | error
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [existing, setExisting] = useState(null);

  // Fetch existing connection status once
  useEffect(() => {
    let cancelled = false;
    whatsappApi
      .getStatus()
      .then((res) => {
        if (!cancelled && res?.success && res.connected) setExisting(res);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize the embedded-signup widget
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setStatus('loading');
      setError(null);
      try {
        const res = await whatsappApi.getConnectToken();
        if (!res?.success || !res.connectToken) {
          throw new Error(res?.error || t('connect.noToken', 'Could not get a connect token'));
        }
        await loadSdk();
        if (cancelled) return;

        instanceRef.current = window.ChakraWhatsappConnect.init({
          connectToken: res.connectToken,
          container: '#chakra-connect',
          onReady: () => !cancelled && setStatus('ready'),
          onMessage: (event, data) => console.log('[chakra-connect]', event, data),
          onError: (err) => {
            if (cancelled) return;
            setError(err?.message || String(err) || t('connect.error', 'Connection error'));
            setStatus('error');
          },
          onSuccess: async (data) => {
            if (cancelled) return;
            setResult(data);
            setStatus('success');
            try {
              await whatsappApi.saveConnection(data);
            } catch (e) {
              console.error('Failed to persist connection', e);
            }
          },
        });
      } catch (e) {
        if (!cancelled) {
          setError(
            e.response?.data?.error ||
              e.response?.data?.message ||
              e.message ||
              String(e)
          );
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        instanceRef.current?.destroy?.();
      } catch (_) {
        /* noop */
      }
    };
  }, [attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`connect-wa ${isRTL ? 'rtl' : ''}`}>
      <div className="connect-wa-card">
        <div className="connect-wa-head">
          <div className="connect-wa-badge">
            <MessageCircle size={26} />
          </div>
          <div>
            <h1>{t('connect.title', 'Connect WhatsApp')}</h1>
            <p>
              {t(
                'connect.subtitle',
                'Link your WhatsApp Business number so RodBot can send and receive messages on your behalf.'
              )}
            </p>
          </div>
        </div>

        {existing && status !== 'success' && (
          <div className="connect-wa-current">
            <span className="connect-wa-dot" />
            {t('connect.connected', 'Connected')}
            {existing.store_number ? ` · ${existing.store_number}` : ''}
            {' — '}
            {t('connect.connectAnother', 'connect another number below to add or replace it.')}
          </div>
        )}

        <ul className="connect-wa-steps">
          <li>{t('connect.step1', 'Click "Connect with Facebook" below.')}</li>
          <li>{t('connect.step2', 'Pick (or create) your WhatsApp Business account.')}</li>
          <li>{t('connect.step3', 'Verify your phone number with the code Meta sends you.')}</li>
        </ul>

        {status === 'loading' && (
          <div className="connect-wa-state">
            {t('connect.preparing', 'Preparing secure connection…')}
          </div>
        )}

        {status === 'error' && (
          <div className="connect-wa-error">
            <div>
              <AlertTriangle size={16} /> {error}
            </div>
            <button onClick={() => setAttempt((a) => a + 1)}>
              <RefreshCw size={15} /> {t('connect.tryAgain', 'Try again')}
            </button>
          </div>
        )}

        {status === 'success' && (
          <div className="connect-wa-success">
            <div className="connect-wa-success-title">
              <CheckCircle2 size={18} /> {t('connect.success', 'WhatsApp connected!')}
            </div>
            {phoneFromResult(result) && (
              <div className="connect-wa-success-num">
                {t('connect.number', 'Number')}: {phoneFromResult(result)}
              </div>
            )}
            <p>{t('connect.allSet', "You're all set. You can close this page.")}</p>
          </div>
        )}

        {/* Chakra renders the "Connect with Facebook" button in here */}
        <div
          id="chakra-connect"
          className="connect-wa-widget"
          style={{ display: status === 'success' ? 'none' : 'block' }}
        />
      </div>
    </div>
  );
};

export default ConnectWhatsAppPage;
